import { ChartDataSeries } from '../../../infrastructure/widgets/cards/ChartCard';

export interface ProgressChartProps {
  categories: string[];
  series: ChartDataSeries[];
}

/**
 * ProgressChart (Legacy DOM Replication)
 * 
 * Replicates the production 'ph-container' (Pipeline Health).
 * Renders a 1D horizontal stacked progress bar with a legend.
 */
export function ProgressChart({ categories, series }: ProgressChartProps) {
  const counts = series[0]?.data || [];
  
  // Production behavior maps categories to specific colors and statuses
  // The expected categories for Pipeline Health are: ['new', 'contacted', 'interested', 'converted', 'rejected']
  const colorMap: Record<string, string> = {
    new: 'bg-surface-container-highest',
    contacted: 'bg-primary/40',
    interested: 'bg-primary/70',
    converted: 'bg-success',
    rejected: 'bg-danger/80'
  };

  const labelMap: Record<string, string> = {
    new: 'New',
    contacted: 'Contacted',
    interested: 'Interested',
    converted: 'Converted',
    rejected: 'Rejected'
  };

  const totalStatus = counts.reduce((a, b) => a + b, 0) || 1;

  const getPct = (n: number) => Math.round((n / totalStatus) * 100);

  return (
    <div className="flex-1 flex flex-col justify-center gap-md w-full h-full">
      {/* Stacked bar */}
      <div className="h-8 w-full flex rounded overflow-hidden bg-surface-variant/30">
        {counts.every(n => !n) ? (
          <div className="w-full h-full bg-surface-variant"></div>
        ) : categories.map((cat, i) => {
          const n = counts[i] || 0;
          if (n === 0) return null;
          
          const pct = getPct(n);
          const colorClass = colorMap[cat] || 'bg-surface-variant';
          const title = labelMap[cat] || cat;
          
          // Legacy added left border to all but first item, but we handle via map index 
          // (Actually legacy hardcoded border-l for contacted/interested/converted/rejected)
          const borderClass = cat !== 'new' ? 'border-l border-surface' : '';
          
          return (
            <div 
              key={cat} 
              className={`${colorClass} ${borderClass}`} 
              style={{ width: `${pct}%` }} 
              title={title}
            ></div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex flex-col gap-sm">
        {categories.map((cat, i) => {
          const n = counts[i] || 0;
          const pct = getPct(n);
          const colorClass = colorMap[cat] || 'bg-surface-variant';
          const label = labelMap[cat] || cat;

          return (
            <div key={cat} className="flex justify-between items-center text-body-sm">
              <div className="flex items-center gap-sm">
                <div className={`w-3 h-3 rounded-sm ${colorClass.replace('border-l border-surface', '')}`}></div>
                <span>{label}</span>
              </div>
              <span className="text-on-surface-variant text-[11px]">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
